// src/pages/AdminServices.jsx
import React, { useEffect, useMemo, useState } from "react";
import AddCategoryModal from "./AddCategoryModal";
import AddSubcategoryModal from "./AddSubcategoryModal/AddSubcategoryModal";
import EditServiceModal from "./AddSubcategoryModal/EditServiceModal";

// APIs
import {
  subscribeCategories,
  subscribeSubcategories,
  subscribeServiceItems,
  createServiceFromAdminForm,
  updateServiceItem,
  setServiceItemActive,
  deleteServiceItem,
} from "../lib/servicesApi";

const ADD_CAT_SENTINEL = "__ADD_NEW_CATEGORY__";
const ADD_SUBCAT_SENTINEL = "__ADD_NEW_SUBCATEGORY__";

const AdminServices = () => {
  /* ---------- Categories ---------- */
  const [cats, setCats] = useState([]);
  const [catError, setCatError] = useState(null);

  useEffect(() => {
    const unsub = subscribeCategories?.(setCats, setCatError);
    return () => unsub && unsub();
  }, []);

  const activeCats = useMemo(() => cats.filter((c) => c.active), [cats]);

  /* ---------- Subcategories ---------- */
  const [subcats, setSubcats] = useState([]);
  const [subcatError, setSubcatError] = useState(null);

  useEffect(() => {
    const unsub = subscribeSubcategories?.(setSubcats, setSubcatError);
    return () => unsub && unsub();
  }, []);

  const activeSubcats = useMemo(() => subcats.filter((s) => s.active), [subcats]);

  const subcatsByCat = useMemo(() => {
    const map = new Map();
    activeSubcats.forEach((s) => {
      const arr = map.get(s.categoryKey) || [];
      arr.push(s);
      map.set(s.categoryKey, arr);
    });
    // sort each bucket by label
    for (const [k, v] of map) {
      v.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
      map.set(k, v);
    }
    return map;
  }, [activeSubcats]);

  /* ---------- Services ---------- */
  const [filterCat, setFilterCat] = useState("all");
  const [filterSubcat, setFilterSubcat] = useState("all");
  const [svcList, setSvcList] = useState([]);
  const [svcError, setSvcError] = useState(null);
  const [svcSaving, setSvcSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const [svcForm, setSvcForm] = useState({
    category: "",
    subcategory: "", // NEW
    type: "",
    title: "",
    desc: "",
    icon: "bi-tools",
    price: "",
    active: true,
  });

  // Subscribe services with both filters
  useEffect(() => {
    const unsub = subscribeServiceItems(
      setSvcList,
      setSvcError,
      {
        category: filterCat === "all" ? undefined : filterCat,
        subcategory: filterSubcat === "all" ? undefined : filterSubcat,
      }
    );
    return () => unsub();
  }, [filterCat, filterSubcat]);

  // default category in service form: first active category
  useEffect(() => {
    if (!svcForm.category) {
      const firstActive = activeCats[0];
      if (firstActive) {
        setSvcForm((f) => ({ ...f, category: firstActive.key }));
      }
    }
  }, [activeCats, svcForm.category]);

  // when category changes in form, default subcategory (first active in that category)
  useEffect(() => {
    if (!svcForm.category) return;
    const firstSub = (subcatsByCat.get(svcForm.category) || [])[0];
    setSvcForm((f) => ({ ...f, subcategory: firstSub?.key || "" }));
  }, [svcForm.category, subcatsByCat]);

  /* ---------- Modals for adding category/subcategory ---------- */
  const [showAddCatModal, setShowAddCatModal] = useState(false);
  const [showAddSubcatModal, setShowAddSubcatModal] = useState(false);

  const onSvcChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "category") {
      if (value === ADD_CAT_SENTINEL) {
        setShowAddCatModal(true);
        return;
      }
      // reset subcategory when category changed
      setSvcForm((f) => ({ ...f, category: value, subcategory: "" }));
      return;
    }

    if (name === "subcategory") {
      if (value === ADD_SUBCAT_SENTINEL) {
        setShowAddSubcatModal(true);
        return;
      }
    }

    setSvcForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onCategoryCreated = (createdKey) => {
    // after adding a category, preselect it and clear subcategory
    setSvcForm((f) => ({ ...f, category: createdKey, subcategory: "" }));
  };

  const onSubcategoryCreated = (createdKey, parentCatKey) => {
    // preselect both parent category and the new subcategory
    setSvcForm((f) => ({ ...f, category: parentCatKey, subcategory: createdKey }));
  };

  const onSvcCreate = async (e) => {
    e.preventDefault();
    setSvcError(null);

    const { category, title, price } = svcForm;
    if (!category || !title || price == null || String(price).trim() === "") {
      setSvcError(new Error("Please fill category, title and price."));
      return;
    }

    setSvcSaving(true);
    try {
      await createServiceFromAdminForm(svcForm);
      // reset form but keep default category as first active
      setSvcForm({
        category: activeCats[0]?.key || "",
        subcategory: "",
        type: "",
        title: "",
        desc: "",
        icon: "bi-tools",
        price: "",
        active: true,
      });
      setShowAddForm(false);
    } catch (err) {
      setSvcError(err);
    } finally {
      setSvcSaving(false);
    }
  };

  // Filter subcategories in filter toolbar based on selected filterCat
  const filterSubcatsForCat = useMemo(() => {
    if (filterCat === "all") return [];
    return subcatsByCat.get(filterCat) || [];
  }, [filterCat, subcatsByCat]);

  // Reset subcategory filter when category filter changes
  useEffect(() => {
    setFilterSubcat("all");
  }, [filterCat]);

  /* ---------- Edit modal ---------- */
  const [editing, setEditing] = useState(null); // service being edited or null

  return (
    <section>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Services</h3>
        {!showAddForm ? (
          <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>
            <i className="bi bi-plus-lg me-1" /> Add Service
          </button>
        ) : (
          <button className="btn btn-outline-secondary" onClick={() => setShowAddForm(false)}>
            <i className="bi bi-arrow-left me-1" /> Back to List
          </button>
        )}
      </div>
      <hr />

      {svcError && <div className="alert alert-danger">{svcError.message}</div>}
      {catError && <div className="alert alert-danger">{catError.message}</div>}
      {subcatError && <div className="alert alert-danger">{subcatError.message}</div>}

      {/* ---------- Add Service Form ---------- */}
      {
        showAddForm && (
          <div className="card mb-4 shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Add Service</h5>
              <form onSubmit={onSvcCreate}>
                <div className="row g-3">
                  {/* Category select */}
                  <div className="col-12 col-md-4">
                    <label className="form-label" htmlFor="category">Category</label>
                    <select
                      id="category"
                      name="category"
                      className="form-select"
                      value={svcForm.category}
                      onChange={onSvcChange}
                      required
                    >
                      {activeCats.length === 0 ? (
                        <>
                          <option value="">— No categories —</option>
                          <option value={ADD_CAT_SENTINEL}>➕ Add new category…</option>
                        </>
                      ) : (
                        <>
                          {activeCats.map((c) => (
                            <option key={c.id} value={c.key}>{c.label}</option>
                          ))}
                          <option value={ADD_CAT_SENTINEL}>➕ Add new category…</option>
                        </>
                      )}
                    </select>
                  </div>

                  {/* Subcategory select */}
                  <div className="col-12 col-md-4">
                    <label className="form-label" htmlFor="subcategory">Subcategory</label>
                    <select
                      id="subcategory"
                      name="subcategory"
                      className="form-select"
                      value={svcForm.subcategory}
                      onChange={onSvcChange}
                      disabled={!svcForm.category}
                    >
                      {!svcForm.category ? (
                        <option value="">— Select category first —</option>
                      ) : (subcatsByCat.get(svcForm.category) || []).length === 0 ? (
                        <>
                          <option value="">— No subcategories —</option>
                          <option value={ADD_SUBCAT_SENTINEL}>➕ Add new subcategory…</option>
                        </>
                      ) : (
                        <>
                          <option value="">(None)</option>
                          {(subcatsByCat.get(svcForm.category) || []).map((s) => (
                            <option key={s.id} value={s.key}>{s.label}</option>
                          ))}
                          <option value={ADD_SUBCAT_SENTINEL}>➕ Add new subcategory…</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label" htmlFor="type">Service Slug (optional)</label>
                    <input
                      id="type"
                      name="type"
                      className="form-control"
                      placeholder="e.g., maid-basic"
                      value={svcForm.type}
                      onChange={onSvcChange}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label" htmlFor="title">Title</label>
                    <input
                      id="title"
                      name="title"
                      className="form-control"
                      value={svcForm.title}
                      onChange={onSvcChange}
                      required
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label" htmlFor="desc">Description</label>
                    <textarea
                      id="desc"
                      name="desc"
                      className="form-control"
                      rows={3}
                      value={svcForm.desc}
                      onChange={onSvcChange}
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label" htmlFor="icon">Icon Class</label>
                    <input
                      id="icon"
                      name="icon"
                      className="form-control"
                      placeholder="e.g., bi-person-lines-fill"
                      value={svcForm.icon}
                      onChange={onSvcChange}
                    />
                    <small className="text-muted">Use Bootstrap Icons + <code>text-primary-label</code> in UI.</small>
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label" htmlFor="price">Starting Price (₹)</label>
                    <input
                      id="price"
                      name="price"
                      type="number"
                      min="0"
                      step="1"
                      className="form-control"
                      value={svcForm.price}
                      onChange={onSvcChange}
                      required
                    />
                  </div>

                  <div className="col-12 col-md-4 d-flex align-items-center">
                    <div className="form-check mt-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="active-svc"
                        name="active"
                        checked={svcForm.active}
                        onChange={onSvcChange}
                      />
                      <label className="form-check-label" htmlFor="active-svc">Active</label>
                    </div>
                  </div>

                  <div className="col-12">
                    <button
                      className="btn btn-primary"
                      type="submit"
                      disabled={svcSaving || (!svcForm.category && activeCats.length === 0)}
                    >
                      {svcSaving ? "Saving..." : "Add Service"}
                    </button>
                    {(activeCats.length === 0) && (
                      <small className="text-muted ms-2">Add a category first.</small>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* ---------- Manage Services ---------- */}
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
            <h5 className="card-title mb-0">Services ({svcList.length})</h5>
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0">Filter:</label>
              <select
                className="form-select form-select-sm"
                value={filterCat}
                onChange={(e) => setFilterCat(e.target.value)}
              >
                <option value="all">All Categories</option>
                {cats.map((c) => (
                  <option key={c.id} value={c.key}>{c.label}</option>
                ))}
              </select>
              <select
                className="form-select form-select-sm"
                value={filterSubcat}
                onChange={(e) => setFilterSubcat(e.target.value)}
                disabled={filterCat === "all"}
              >
                {filterCat === "all" ? (
                  <option value="all">All Subcategories</option>
                ) : filterSubcatsForCat.length === 0 ? (
                  <option value="all">No subcategories</option>
                ) : (
                  <>
                    <option value="all">All Subcategories</option>
                    {filterSubcatsForCat.map((s) => (
                      <option key={s.id} value={s.key}>{s.label}</option>
                    ))}
                  </>
                )}
              </select>
            </div>
          </div>

          {svcList.length === 0 ? (
            <p className="text-muted mb-0">No services yet.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Icon</th>
                    <th>Name</th>
                    <th>Slug</th>
                    <th>Category</th>
                    <th>Subcategory</th>
                    <th>Price</th>
                    <th>Active</th>
                    <th style={{ width: 220 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {svcList.map((s) => {
                    const catLabel =
                      cats.find((c) => c.key === s.category)?.label || s.category;
                    const subcatLabel =
                      subcats.find((sc) => sc.key === s.subcategory && sc.categoryKey === s.category)?.label ||
                      s.subcategory || "-";

                    return (
                      <tr key={s.id}>
                        <td>
                          <i className={`bi ${s.icon || "bi-tools"} text-primary-label`} aria-hidden="true" />
                        </td>
                        <td>{s.name}</td>
                        <td><code>{s.slug || "-"}</code></td>
                        <td>{catLabel}</td>
                        <td>{subcatLabel}</td>
                        <td>₹{s.price}</td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`svcactive-${s.id}`}
                              checked={!!s.isActive}
                              onChange={(e) => setServiceItemActive(s.id, e.target.checked)}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              type="button"
                              onClick={() => setEditing(s)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              type="button"
                              onClick={async () => {
                                const ok = window.confirm(`Delete "${s.name}"?`);
                                if (ok) await deleteServiceItem(s.id);
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add Category popup (triggered from selects) */}
      <AddCategoryModal
        show={showAddCatModal}
        onClose={() => setShowAddCatModal(false)}
        onCreated={onCategoryCreated}
      />

      {/* Add Subcategory popup (triggered from selects) */}
      <AddSubcategoryModal
        show={showAddSubcatModal}
        onClose={() => setShowAddSubcatModal(false)}
        onCreated={onSubcategoryCreated}
        categories={cats}
        initialCategoryKey={svcForm.category}
      />

      {/* Edit Service modal */}

      <EditServiceModal
        show={!!editing}
        onClose={() => setEditing(null)}
        service={editing}
        categories={cats}
        subcategories={subcats}
        onSave={async (updates) => {
          if (!editing) return;
          await updateServiceItem(editing.id, updates);
        }}
      />

    </section >
  );
};

export default AdminServices;